import { useDealContext } from '@openx/components-deal/lib/utils/context/DealContext';
import type { UsePackageRetargetingDealsValidation } from '@openx/types/package/PackageHookTypes';
import { TargetingFields } from '@openx/types/targeting/targeting';
import { useCallback } from 'react';
import { hasRetargetingCapableDsp } from '../../targeting/utils/hasRetargetingCapableDsp';

const DISPLAY_LIMIT = 50;

export const usePackageRetargetingDealsValidation: UsePackageRetargetingDealsValidation = accountUid => {
  const { useDemandPartnersFetch } = useDealContext();
  const { mapById, loading } = useDemandPartnersFetch({
    account_uid: accountUid,
    enabled: !!accountUid,
  });

  const getIncompatibleDeals = useCallback<ReturnType<UsePackageRetargetingDealsValidation>['getIncompatibleDeals']>(
    ({ targeting, deals, hasInaccessibleDeals }) => {
      if (loading) {
        return null;
      }

      const hasRetargetingSegment = !!targeting?.[TargetingFields.RETARGETING]?.val;
      if (!hasRetargetingSegment) {
        return null;
      }

      if (deals.length === 0 && !hasInaccessibleDeals) {
        return null;
      }

      const incompatibleDeals = deals
        .filter(deal => !hasRetargetingCapableDsp(deal.deal_participants, mapById))
        .map(deal => ({ name: deal.name, action: 'remove_retargeting' }));

      if (incompatibleDeals.length === 0 && !hasInaccessibleDeals) {
        return null;
      }

      return {
        deals: incompatibleDeals.slice(0, DISPLAY_LIMIT),
        hasMore: incompatibleDeals.length > DISPLAY_LIMIT || hasInaccessibleDeals,
      };
    },
    [loading, mapById]
  );

  return {
    isLoading: !!accountUid && loading,
    getIncompatibleDeals,
  };
};
